'use client';

import { useCallback, useEffect, useMemo, useRef, startTransition } from 'react';
import type {
  InitialMapState,
  MapSearchResultEvent,
  MapSearchStatusEvent,
  MapSelection,
  MapStateChangeEvent,
  MapWidgetState,
  SearchFilters,
} from '@propileya/geo-widget';
import { FiltersType } from '@/types';
import {
  toMapFilters,
  toMapSelection,
  toInitialMapState,
  toSavedMapFilter,
  type SavedMapFilter,
} from '@/features/pages/housing-list/utils/geoWidgetContract';
import type { ReferenceLocation } from '@/types/referenceLocation';
import { buildGoToMapPatch } from '@/features/pages/housing-list/utils/collectAdmunitIds';
import type { IHousingListResponse } from '@/types';

type UseMapFiltersParams = {
  filters: FiltersType;
  requestFilters: Record<string, unknown> | undefined;
  locations: ReferenceLocation[];
  filterData: IHousingListResponse['filterData'] | undefined;
  setFilters: (value: Partial<FiltersType>) => void;
};

type UseMapFiltersReturn = {
  mapFilters: SearchFilters;
  mapSelection: MapSelection;
  mapInitialState: InitialMapState;
  handleReady: (state: MapWidgetState) => void;
  handleStateChange: (event: MapStateChangeEvent) => void;
  handleSearchResult: (event: MapSearchResultEvent) => void;
  handleSearchStatusChange: (event: MapSearchStatusEvent) => void;
  handleGoToMap: () => void;
};

export const useMapFilters = ({
  filters,
  requestFilters,
  locations,
  filterData,
  setFilters,
}: UseMapFiltersParams): UseMapFiltersReturn => {
  const latestSavedMapFilterRef = useRef<SavedMapFilter>({
    admunits: filters.admunits,
    mapArea: filters.mapArea,
  });
  // Keep in sync with URL/profile changes from outside.
  useEffect(() => {
    latestSavedMapFilterRef.current = {
      admunits: filters.admunits,
      mapArea: filters.mapArea,
    };
  }, [filters.admunits, filters.mapArea]);

  const latestMapStateRef = useRef<MapWidgetState | null>(null);

  const mapFilters: SearchFilters = useMemo(() => toMapFilters(requestFilters), [requestFilters]);
  const mapSelection: MapSelection = useMemo(
    () => toMapSelection({ admunits: filters.admunits, mapArea: filters.mapArea }),
    [filters.admunits, filters.mapArea],
  );
  const mapInitialState: InitialMapState = useMemo(
    () => toInitialMapState({ admunits: filters.admunits, mapArea: filters.mapArea }),
    [filters.admunits, filters.mapArea],
  );

  const handleReady = useCallback((state: MapWidgetState) => {
    latestMapStateRef.current = state;
  }, []);

  const handleStateChange = useCallback(
    ({ state, source }: MapStateChangeEvent) => {
      const previousState = latestMapStateRef.current;
      latestMapStateRef.current = state;

      if (source !== 'user') return;

      const selectionChanged =
        JSON.stringify(previousState?.selection) !== JSON.stringify(state.selection);
      const viewportChanged =
        JSON.stringify(previousState?.viewport.bounds) !== JSON.stringify(state.viewport.bounds);

      if (!selectionChanged && !viewportChanged) return;

      startTransition(() => {
        const patch = toSavedMapFilter(state, latestSavedMapFilterRef.current);
        latestSavedMapFilterRef.current = patch;
        setFilters({ ...patch, currentPage: 0 });
      });
    },
    [setFilters],
  );

  // Search result and status are handled in HousingListScreen directly via callbacks,
  // but we expose pass-through handlers here so the hook owns the full map callback surface.
  const handleSearchResult = useCallback((_event: MapSearchResultEvent) => {}, []);
  const handleSearchStatusChange = useCallback((_event: MapSearchStatusEvent) => {}, []);

  const handleGoToMap = useCallback(() => {
    const patch = buildGoToMapPatch({
      currentAdmunits: filters.admunits,
      locations,
      unitListValue: filters.unitListValue,
      filterData,
    });
    setFilters(patch);
  }, [setFilters, filters.admunits, filters.unitListValue, locations, filterData]);

  return {
    mapFilters,
    mapSelection,
    mapInitialState,
    handleReady,
    handleStateChange,
    handleSearchResult,
    handleSearchStatusChange,
    handleGoToMap,
  };
};
